// src/pages/Marketplace/surge-pricing/SurgePricingPage.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import { Env, ENV_COLORS, ErrorBanner, Spinner, getAdminApiUrl } from '@/pages/Admin/shared';
import { useMarkets } from '@/pages/Marketplace/shared';
import CurrentPriceCard from './CurrentPriceCard';
import DayOfWeekTable from './DayOfWeekTable';
import EventsView from './EventsView';

const LS_KEY_ENV = 'marketplace.env';
const LS_KEY_MARKET = 'marketplace.surge.marketId';

export default function SurgePricingPage(): React.ReactElement {
  const { instance, accounts } = useMsal();
  const [env, setEnv] = useState<Env>(() => (localStorage.getItem(LS_KEY_ENV) as Env) || 'dev');
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [marketId, setMarketId] = useState<string | null>(() => localStorage.getItem(LS_KEY_MARKET));

  useEffect(() => { localStorage.setItem(LS_KEY_ENV, env); }, [env]);
  useEffect(() => {
    if (marketId) localStorage.setItem(LS_KEY_MARKET, marketId);
  }, [marketId]);

  useEffect(() => {
    if (accounts.length === 0) return;
    acquireApiToken(instance, accounts[0]).then((accessToken) => ({ accessToken }))
      .then((r) => setAccessToken(r.accessToken))
      .catch(() => setAccessToken(null));
  }, [instance, accounts]);

  const apiUrl = getAdminApiUrl(env);
  const { data: markets, loading, error } = useMarkets(apiUrl, accessToken);

  // Default to first active market
  useEffect(() => {
    if (!markets) return;
    const firstActive = markets.find((m) => m.is_active);
    if (!marketId && firstActive) setMarketId(firstActive.id);
    else if (marketId && !markets.some((m) => m.id === marketId)) {
      setMarketId(firstActive?.id ?? null);
    }
  }, [markets, marketId]);

  const selected = useMemo(
    () => markets?.find((m) => m.id === marketId) ?? null,
    [markets, marketId],
  );
  const envColors = ENV_COLORS[env];

  return (
    <div className="p-6 text-gray-200 min-h-[calc(100vh-72px)]">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">Surge Pricing</h1>
          {env !== 'prod' && (
            <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded border border-amber-700/50 text-amber-300 bg-amber-950/40">
              Stripe test mode
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <select
            value={marketId ?? ''}
            onChange={(e) => setMarketId(e.target.value || null)}
            className="text-sm bg-gray-900 border border-gray-700 rounded px-2 py-1"
          >
            <option value="">— select market —</option>
            {markets?.map((m) => (
              <option key={m.id} value={m.id}>{m.name}{!m.is_active ? ' (inactive)' : ''}</option>
            ))}
          </select>
          <select
            value={env}
            onChange={(e) => setEnv(e.target.value as Env)}
            className={`text-xs px-2 py-1 rounded border ${envColors.pill}`}
          >
            <option value="dev">dev</option>
            <option value="staging">staging</option>
            <option value="prod">prod</option>
          </select>
        </div>
      </div>

      {loading && <Spinner size="sm" />}
      {error && <ErrorBanner message={error} />}

      {!selected ? (
        <div className="text-gray-500 text-sm">Select a market to view surge pricing.</div>
      ) : (
        <div className="space-y-8">
          <CurrentPriceCard market={selected} apiUrl={apiUrl} accessToken={accessToken} />
          <DayOfWeekTable />
          <EventsView market={selected} apiUrl={apiUrl} accessToken={accessToken} />
        </div>
      )}
    </div>
  );
}
